import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import { fmtDateLong } from '../services/dates';
import DocumentsBox from './DocumentsBox';
import './ClientesPage.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate  = d => fmtDateLong(d);
const fmtEur   = n => n !== undefined && n !== null ? Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2 }) + ' €' : '—';
const ini      = s => (s||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();

const STATUS_INV = {
  borrador: { label: 'Borrador', cls: 'cp-inv-s--draft'  },
  enviada:  { label: 'Enviada',  cls: 'cp-inv-s--sent'   },
  pagada:   { label: 'Pagada',   cls: 'cp-inv-s--paid'   },
  vencida:  { label: 'Vencida',  cls: 'cp-inv-s--late'   },
};

// ── InvoiceModal ──────────────────────────────────────────────────────────────
function InvoiceModal({ clientId, proforma, token, onSaved, onClose }) {
  const today30 = new Date(Date.now() + 30*24*3600*1000).toISOString().slice(0,10);
  const [form, setForm] = useState({
    date:           new Date().toISOString().slice(0,10),
    due_date:       today30,
    tax_rate:       21,
    payment_method: 'transferencia',
    notes:          '',
  });
  const [items, setItems] = useState(
    proforma
      ? [{ description: proforma.titulo || 'Presupuesto', quantity: 1, unit_price: Number(proforma.total) || 0 }]
      : [{ description: '', quantity: 1, unit_price: 0 }]
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const subtotal   = items.reduce((s,i) => s + Number(i.quantity)*Number(i.unit_price), 0);
  const tax_amount = Math.round(subtotal * form.tax_rate) / 100;
  const total      = subtotal + tax_amount;

  function setItem(idx, field, val) {
    setItems(prev => prev.map((it,i) => i===idx ? { ...it, [field]: val } : it));
  }
  function addItem()    { setItems(prev => [...prev, { description:'', quantity:1, unit_price:0 }]); }
  function removeItem(idx) { setItems(prev => prev.filter((_,i) => i!==idx)); }

  async function save() {
    if (items.every(i => !i.description.trim())) { setErr('Añade al menos un concepto'); return; }
    setSaving(true); setErr('');
    try {
      const result = await apiPost('/invoices', {
        client_id:      clientId,
        proforma_id:    proforma?.id || null,
        date:           form.date,
        due_date:       form.due_date,
        tax_rate:       Number(form.tax_rate),
        payment_method: form.payment_method,
        notes:          form.notes,
        items:          items.map(i => ({ description: i.description, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })),
      }, token);
      onSaved(result);
    } catch(e) { setErr(e.message); }
    setSaving(false);
  }

  return (
    <div className="cp-overlay" onClick={onClose}>
      <div className="cp-modal cp-modal--inv" onClick={e=>e.stopPropagation()}>
        <div className="cp-modal-head">
          <span>{proforma ? `Factura desde: ${proforma.titulo||'Presupuesto'}` : 'Nueva factura'}</span>
          <button className="cp-modal-x" onClick={onClose}>✕</button>
        </div>
        <div className="cp-modal-body">
          {/* Fechas y opciones */}
          <div className="cp-inv-row3">
            <div className="cp-field">
              <label>Fecha emisión</label>
              <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))}/>
            </div>
            <div className="cp-field">
              <label>Fecha vencimiento</label>
              <input type="date" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))}/>
            </div>
            <div className="cp-field">
              <label>Forma de pago</label>
              <select value={form.payment_method} onChange={e=>setForm(f=>({...f,payment_method:e.target.value}))}>
                <option value="transferencia">Transferencia bancaria</option>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
              </select>
            </div>
          </div>

          {/* Líneas */}
          <div className="cp-inv-items">
            <div className="cp-inv-items-head">
              <span style={{flex:3}}>Descripción</span>
              <span style={{flex:'0 0 70px',textAlign:'center'}}>Cant.</span>
              <span style={{flex:'0 0 110px',textAlign:'right'}}>Precio unit.</span>
              <span style={{flex:'0 0 110px',textAlign:'right'}}>Importe</span>
              <span style={{flex:'0 0 30px'}}></span>
            </div>
            {items.map((it,idx) => (
              <div key={idx} className="cp-inv-item-row">
                <input className="cp-inv-desc" style={{flex:3}} placeholder="Concepto…" value={it.description}
                  onChange={e=>setItem(idx,'description',e.target.value)}/>
                <input className="cp-inv-qty" type="number" min="0" step="0.01" value={it.quantity}
                  onChange={e=>setItem(idx,'quantity',e.target.value)}/>
                <input className="cp-inv-price" type="number" min="0" step="0.01" value={it.unit_price}
                  onChange={e=>setItem(idx,'unit_price',e.target.value)}/>
                <span className="cp-inv-line-total">{fmtEur(Number(it.quantity)*Number(it.unit_price))}</span>
                {items.length>1 && <button className="cp-inv-rm" onClick={()=>removeItem(idx)}>✕</button>}
              </div>
            ))}
            <button className="cp-inv-add-line" onClick={addItem}>+ Añadir línea</button>
          </div>

          {/* IVA y totales */}
          <div className="cp-inv-totals-wrap">
            <div className="cp-field cp-field--sm">
              <label>% IVA</label>
              <input type="number" min="0" max="100" step="1" value={form.tax_rate}
                onChange={e=>setForm(f=>({...f,tax_rate:e.target.value}))} style={{width:80}}/>
            </div>
            <div className="cp-inv-summary">
              <div className="cp-inv-sum-row"><span>Base imponible</span><strong>{fmtEur(subtotal)}</strong></div>
              <div className="cp-inv-sum-row"><span>IVA ({form.tax_rate}%)</span><strong>{fmtEur(tax_amount)}</strong></div>
              <div className="cp-inv-sum-row cp-inv-sum-row--total"><span>TOTAL</span><strong>{fmtEur(total)}</strong></div>
            </div>
          </div>

          <div className="cp-field">
            <label>Observaciones</label>
            <textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Opcional…"/>
          </div>

          {err && <p className="cp-err">{err}</p>}
        </div>
        <div className="cp-modal-foot">
          <button className="cp-btn cp-btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="cp-btn cp-btn--primary" onClick={save} disabled={saving}>
            {saving ? 'Guardando…' : 'Crear factura'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ClientDrawer ──────────────────────────────────────────────────────────────
export function ClientDrawer({ clientId, token, onClose, onUpdated, onGenerarPresupuesto }) {
  const [data,    setData]    = useState(null);
  const [tab,     setTab]     = useState('datos');
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({});
  const [note,    setNote]    = useState('');
  const [invModal, setInvModal] = useState(null); // null | 'new' | proforma obj
  const [saving,  setSaving]  = useState(false);
  const noteRef = useRef();

  const load = useCallback(async () => {
    const d = await apiGet(`/clients/${clientId}`, token);
    setData(d);
    setForm({
      name: d.name||'', company_name: d.company_name||'', cif: d.cif||'',
      email: d.email||'', phone: d.phone||'', address: d.address||'',
      postal_code: d.postal_code||'', city: d.city||'', province: d.province||'',
      country: d.country||'España',
    });
  }, [clientId, token]);

  useEffect(() => { load(); }, [load]);

  async function saveData() {
    setSaving(true);
    await apiPut(`/clients/${clientId}`, form, token);
    setSaving(false); setEditing(false); load(); onUpdated();
  }

  async function addNote() {
    if (!note.trim()) return;
    await apiPost(`/clients/${clientId}/notes`, { content: note }, token);
    setNote(''); load();
  }

  async function deleteNote(id) {
    await apiDelete(`/clients/notes/${id}`, token);
    load();
  }

  async function changeInvStatus(invId, status) {
    await apiPut(`/invoices/${invId}`, { status }, token);
    load();
  }

  function viewPdf(inv) {
    apiGet(`/invoices/${inv.id}`, token).then(full => {
      const w = window.open('', '_blank');
      w.document.write(full.pdf_html);
      w.document.close();
      setTimeout(() => w.print(), 600);
    });
  }

  function downloadPdf(inv) {
    apiGet(`/invoices/${inv.id}`, token).then(full => {
      const blob = new Blob([full.pdf_html], { type: 'text/html;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${inv.invoice_number}.html`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  function handleInvSaved() {
    setInvModal(null); load();
  }

  // Ver/imprimir el HTML de una proforma
  function viewProforma(p) {
    apiGet(`/proformas/${p.id}`, token).then(full => {
      const w = window.open('', '_blank');
      w.document.write(full.html || '');
      w.document.close();
      setTimeout(() => w.print(), 600);
    }).catch(e => alert(e.message));
  }

  // Descargar la proforma como archivo HTML (imprimible a PDF)
  function downloadProforma(p) {
    apiGet(`/proformas/${p.id}`, token).then(full => {
      const blob = new Blob([full.html || ''], { type: 'text/html;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${(p.titulo || 'presupuesto').replace(/[^\w\-]+/g, '_')}.html`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(e => alert(e.message));
  }

  if (!data) return <div className="cp-drawer"><div className="cp-drawer-loading">Cargando…</div></div>;

  const displayName = data.company_name || data.name || '—';

  return (
    <div className="cp-drawer">
      <div className="cp-drawer-head">
        <div className="cp-drawer-av">{ini(displayName)}</div>
        <div className="cp-drawer-info">
          <div className="cp-drawer-name">{displayName}</div>
          {data.company_name && data.name && <div className="cp-drawer-sub">{data.name}</div>}
          {data.email && <div className="cp-drawer-sub">{data.email}</div>}
        </div>
        <button className="cp-drawer-close" onClick={onClose}>✕</button>
      </div>

      {/* Acción rápida: generar presupuesto desde la ficha del cliente */}
      {onGenerarPresupuesto && (
        <div className="cp-drawer-actions" style={{padding:'0 20px 14px'}}>
          <button className="cp-btn cp-btn--primary cp-btn--sm" style={{width:'100%',justifyContent:'center'}}
            onClick={()=>onGenerarPresupuesto(data)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" style={{marginRight:6,verticalAlign:'-2px'}}>
              <path d="M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1-4h-4v4h4V3z"/>
            </svg>
            Generar presupuesto
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="cp-dtabs">
        {[['datos','Datos'],['proformas','Presupuestos'],['facturas','Facturas'],['documentos','Documentos'],['notas','Notas']].map(([k,l])=>(
          <button key={k} className={`cp-dtab${tab===k?' cp-dtab--on':''}`} onClick={()=>setTab(k)}>
            {l}
            {k==='proformas' && data.proformas?.length>0 && <span className="cp-dtab-n">{data.proformas.length}</span>}
            {k==='facturas'  && data.invoices?.length>0  && <span className="cp-dtab-n">{data.invoices.length}</span>}
            {k==='notas'     && data.notes?.length>0     && <span className="cp-dtab-n">{data.notes.length}</span>}
          </button>
        ))}
      </div>

      {/* TAB: DATOS */}
      {tab==='datos' && (
        <div className="cp-tab-body">
          {editing ? (
            <>
              <div className="cp-form-2">
                {[['name','Nombre contacto'],['company_name','Empresa'],['cif','CIF/NIF'],['email','Email'],
                  ['phone','Teléfono'],['address','Dirección'],['postal_code','C.P.'],['city','Ciudad'],
                  ['province','Provincia'],['country','País']].map(([k,l])=>(
                  <div key={k} className="cp-field">
                    <label>{l}</label>
                    <input value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={l}/>
                  </div>
                ))}
              </div>
              <div className="cp-form-btns">
                <button className="cp-btn cp-btn--ghost" onClick={()=>setEditing(false)}>Cancelar</button>
                <button className="cp-btn cp-btn--primary" onClick={saveData} disabled={saving}>{saving?'Guardando…':'Guardar'}</button>
              </div>
            </>
          ) : (
            <>
              <dl className="cp-dl">
                {[['Empresa',data.company_name],['Nombre',data.name],['CIF/NIF',data.cif],
                  ['Email',data.email],['Teléfono',data.phone],['Dirección',data.address],
                  ['C.P.',data.postal_code],['Ciudad',data.city],['Provincia',data.province],
                  ['País',data.country],['Alta',fmtDate(data.created_at)]].map(([k,v])=> v ? (
                  <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>
                ) : null)}
              </dl>
              <button className="cp-btn cp-btn--ghost cp-btn--sm" onClick={()=>setEditing(true)}>Editar datos</button>
            </>
          )}
        </div>
      )}

      {/* TAB: PRESUPUESTOS */}
      {tab==='proformas' && (
        <div className="cp-tab-body">
          {!data.proformas?.length
            ? <p className="cp-empty-tab">No hay presupuestos asociados a este cliente.</p>
            : data.proformas.map(p => (
              <div key={p.id} className="cp-proforma-row">
                <div className="cp-proforma-info">
                  <span className="cp-proforma-title">{p.titulo || 'Presupuesto'}</span>
                  <span className="cp-muted">{fmtDate(p.created_at)} · {p.tipo === 'financiado' ? 'Financiado' : 'Contado'}</span>
                </div>
                <div className="cp-proforma-right">
                  <span className="cp-proforma-total">{fmtEur(p.total)}</span>
                  <button className="cp-btn cp-btn--xs cp-btn--ghost" onClick={()=>viewProforma(p)} title="Ver e imprimir">Ver</button>
                  <button className="cp-btn cp-btn--xs cp-btn--ghost" onClick={()=>downloadProforma(p)} title="Descargar PDF">↓ PDF</button>
                  <button className="cp-btn cp-btn--xs cp-btn--primary" onClick={()=>setInvModal(p)}>
                    Facturar
                  </button>
                </div>
              </div>
            ))
          }
          <button className="cp-btn cp-btn--ghost cp-btn--sm" style={{marginTop:'12px'}} onClick={()=>setInvModal('new')}>
            + Nueva factura manual
          </button>
        </div>
      )}

      {/* TAB: FACTURAS */}
      {tab==='facturas' && (
        <div className="cp-tab-body">
          <div className="cp-tab-toolbar">
            <button className="cp-btn cp-btn--primary cp-btn--sm" onClick={()=>setInvModal('new')}>+ Nueva factura</button>
          </div>
          {!data.invoices?.length
            ? <p className="cp-empty-tab">No hay facturas aún.</p>
            : data.invoices.map(inv => (
              <div key={inv.id} className="cp-inv-row">
                <div className="cp-inv-row-top">
                  <div className="cp-inv-row-left">
                    <span className="cp-inv-num">{inv.invoice_number}</span>
                    <span className={`cp-inv-badge ${STATUS_INV[inv.status]?.cls||''}`}>{STATUS_INV[inv.status]?.label||inv.status}</span>
                  </div>
                  <span className="cp-inv-total">{fmtEur(inv.total)}</span>
                </div>
                <div className="cp-inv-row-bottom">
                  <div className="cp-inv-row-left">
                    <span className="cp-muted">{fmtDate(inv.date)}{inv.due_date ? ` · vence ${fmtDate(inv.due_date)}` : ''}</span>
                  </div>
                  <div className="cp-inv-row-right">
                    <select className="cp-inv-sel" value={inv.status} onChange={e=>changeInvStatus(inv.id,e.target.value)}>
                      <option value="borrador">Borrador</option>
                      <option value="enviada">Enviada</option>
                      <option value="pagada">Pagada</option>
                      <option value="vencida">Vencida</option>
                    </select>
                    <button className="cp-btn cp-btn--xs cp-btn--ghost" onClick={()=>viewPdf(inv)} title="Ver e imprimir">Ver</button>
                    <button className="cp-btn cp-btn--xs cp-btn--primary" onClick={()=>downloadPdf(inv)} title="Descargar HTML">↓ PDF</button>
                  </div>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* TAB: NOTAS */}
      {tab==='notas' && (
        <div className="cp-tab-body">
          <div className="cp-note-add">
            <textarea ref={noteRef} rows={3} value={note} onChange={e=>setNote(e.target.value)}
              placeholder="Escribe una nota… (Ctrl+Enter para guardar)"
              onKeyDown={e=>{ if(e.key==='Enter'&&e.ctrlKey){ e.preventDefault(); addNote(); }}}/>
            <button className="cp-btn cp-btn--primary cp-btn--sm" onClick={addNote}>Añadir nota</button>
          </div>
          {data.notes?.length===0 && <p className="cp-empty-tab">Sin notas.</p>}
          {data.notes?.map(n=>(
            <div key={n.id} className="cp-note-card">
              <div className="cp-note-head">
                <strong>{n.user_name||'Sistema'}</strong>
                <span className="cp-muted">{fmtDate(n.created_at)}</span>
                <button className="cp-note-del" onClick={()=>deleteNote(n.id)}>✕</button>
              </div>
              <p>{n.content}</p>
            </div>
          ))}
        </div>
      )}

      {tab==='documentos' && (
        <div className="cp-tab-body">
          <DocumentsBox entityType="client" entityId={clientId} token={token} />
        </div>
      )}

      {/* Modal factura */}
      {invModal && (
        <InvoiceModal
          clientId={clientId}
          proforma={invModal!=='new' ? invModal : null}
          token={token}
          onSaved={handleInvSaved}
          onClose={()=>setInvModal(null)}
        />
      )}
    </div>
  );
}

// ── ClientesPage ──────────────────────────────────────────────────────────────
export default function ClientesPage({ onGenerarPresupuesto }) {
  const { auth } = useContext(AuthContext);
  const token    = auth.token;
  const isSuperadmin = auth.user?.role === 'superadmin';
  const [clients,    setClients]    = useState([]);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(true);
  const [drawer,     setDrawer]     = useState(null); // client id
  const [showNew,    setShowNew]    = useState(false);
  const emptyNewForm = { name:'', phone:'', email:'', company_name:'', cif:'', address:'', postal_code:'', city:'', notes:'' };
  const [newForm,    setNewForm]    = useState(emptyNewForm);
  const [newErr,     setNewErr]     = useState('');
  const [saving,     setSaving]     = useState(false);
  const [tenants,    setTenants]    = useState([]);
  const [viewTenant, setViewTenant] = useState('');

  useEffect(() => {
    if (isSuperadmin) apiGet('/tenants', token).then(setTenants).catch(() => {});
  }, [isSuperadmin, token]);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (isSuperadmin && viewTenant) p.set('view_tenant_id', viewTenant);
    const data = await apiGet(`/clients?${p}`, token);
    setClients(data);
    setLoading(false);
  }, [search, token, isSuperadmin, viewTenant]);

  useEffect(() => { load(); }, [load]);

  async function createClient() {
    if (!newForm.name?.trim())  { setNewErr('El nombre completo es obligatorio'); return; }
    if (!newForm.phone?.trim()) { setNewErr('El teléfono es obligatorio'); return; }
    setSaving(true); setNewErr('');
    try {
      // Cliente manual: no proviene de ningún lead (lead_id = null)
      const { id } = await apiPost('/clients', { ...newForm, lead_id: null }, token);
      setShowNew(false);
      setNewForm(emptyNewForm);
      await load();
      setDrawer(id);
    } catch(e) { setNewErr(e.message); }
    setSaving(false);
  }

  async function deleteClient(id) {
    if (!window.confirm('¿Eliminar este cliente?')) return;
    await apiDelete(`/clients/${id}`, token);
    if (drawer===id) setDrawer(null);
    load();
  }

  return (
    <div className={`cp-page${drawer?' cp-page--drawer':''}`}>
      <div className="cp-main">
        {/* Header */}
        <div className="cp-header">
          <div>
            <h1 className="cp-title">Clientes</h1>
            <p className="cp-sub">{clients.length} cliente{clients.length!==1?'s':''}</p>
          </div>
          <button className="cp-btn cp-btn--primary" onClick={()=>setShowNew(true)}>+ Nuevo cliente</button>
        </div>

        {/* Búsqueda */}
        <div className="cp-filters">
          {isSuperadmin && tenants.length > 0 && (
            <select className="cp-sel" value={viewTenant} onChange={e => setViewTenant(e.target.value)}>
              <option value="">Todas las empresas</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <input className="cp-search" placeholder="🔍  Nombre, empresa, email, teléfono, CIF…"
            value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>

        {/* Lista */}
        <div className="cp-list">
          {loading ? (
            <div className="cp-empty">Cargando clientes…</div>
          ) : clients.length===0 ? (
            <div className="cp-empty">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
              </svg>
              <p>No hay clientes{search?' con esa búsqueda':''}.</p>
            </div>
          ) : clients.map(c => (
            <div key={c.id} className={`cp-client-row${drawer===c.id?' cp-client-row--on':''}`}
              onClick={()=>setDrawer(c.id===drawer?null:c.id)}>
              <div className="cp-client-av">{ini(c.company_name||c.name)}</div>
              <div className="cp-client-info">
                <div className="cp-client-name">{c.company_name || c.name || '—'}</div>
                {c.company_name && c.name && <div className="cp-client-sub">{c.name}</div>}
                <div className="cp-client-meta">
                  {c.email && <span>{c.email}</span>}
                  {c.phone && <span>{c.phone}</span>}
                  {c.city  && <span>{c.city}</span>}
                </div>
              </div>
              <div className="cp-client-right">
                {c.cif && <span className="cp-cif">{c.cif}</span>}
                <span className="cp-muted">{fmtDate(c.created_at)}</span>
                <button className="cp-del" onClick={e=>{e.stopPropagation();deleteClient(c.id)}} title="Eliminar">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4h6v2"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Drawer */}
      {drawer && (
        <ClientDrawer
          clientId={drawer}
          token={token}
          onClose={()=>setDrawer(null)}
          onUpdated={load}
          onGenerarPresupuesto={onGenerarPresupuesto}
        />
      )}

      {/* Modal nuevo cliente */}
      {showNew && (
        <div className="cp-overlay" onClick={()=>setShowNew(false)}>
          <div className="cp-modal" onClick={e=>e.stopPropagation()}>
            <div className="cp-modal-head">
              <span>Nuevo cliente</span>
              <button className="cp-modal-x" onClick={()=>setShowNew(false)}>✕</button>
            </div>
            <div className="cp-modal-body">
              <div className="cp-form-2">
                {[['name','Nombre completo',true],['phone','Teléfono',true],
                  ['email','Email'],['company_name','Empresa'],['cif','CIF/NIF'],
                  ['address','Dirección'],['postal_code','Código postal'],['city','Ciudad']].map(([k,l,req])=>(
                  <div key={k} className="cp-field">
                    <label>{l}{req && <span className="cp-optional"> *</span>}</label>
                    <input value={newForm[k]||''} onChange={e=>setNewForm(f=>({...f,[k]:e.target.value}))} placeholder={l}/>
                  </div>
                ))}
              </div>
              <div className="cp-field">
                <label>Notas</label>
                <textarea rows={3} value={newForm.notes||''} onChange={e=>setNewForm(f=>({...f,notes:e.target.value}))} placeholder="Opcional…"/>
              </div>
              {newErr && <p className="cp-err">{newErr}</p>}
            </div>
            <div className="cp-modal-foot">
              <button className="cp-btn cp-btn--ghost" onClick={()=>setShowNew(false)}>Cancelar</button>
              <button className="cp-btn cp-btn--primary" onClick={createClient} disabled={saving}>{saving?'Guardando…':'Crear cliente'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
